import { PaletteColorOptions, Theme, ThemeOptions } from "@material-ui/core";
import { createTheme } from "@material-ui/core/styles";
import createBreakpoints from "@material-ui/core/styles/createBreakpoints";
import { colors } from "@material-ui/core";
import { Overrides } from "@material-ui/core/styles/overrides";
import { Palette, PaletteColor, TypeText } from "@material-ui/core/styles/createPalette";

export const pxToRem = (value: number) => {
    return `${value / 16}rem`;
};
const getTheme = (): Theme => {
    return createTheme(appTheme as ThemeOptions);
};
//Define your custom breakpoints here or accept the default values which are the standard common breakpoints
let customBreakpointValues = {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
};

export const customBreakpoints = createBreakpoints({ values: customBreakpointValues });
const appTheme = createTheme({
    customBreakpointValues,
    //Removes all shadows as per the designs
    ///**************
    ///Set up your spacing here according to Daves designs
    ///Dave tends to do multiples of 10. so p={1} will be 10px padding. p(5) will be 50px padding
    ///The code below will shrink the padding on small devices
    ///**************
    spacing: (value: number) => {
        let spacing = 10; //Default spacing on desktop
        const screenWidth = document.body.clientWidth;
        if (screenWidth <= appTheme.breakpoints.values["md"]) spacing /= 2;
        if (screenWidth > appTheme.breakpoints.values["md"] && screenWidth <= appTheme.breakpoints.values["lg"]) spacing /= 1.5;
        if (screenWidth > appTheme.breakpoints.values["lg"]) spacing /= 1;
        return value * spacing;
    },
    ///**************
    ///Set up your text sizes here to match Dave's designs
    ///You can also set up the values text should be on mobile devices if differenct
    ///**************
    typography: {
        fontFamily: `"Roboto", sans-serif`,
        h1: {
            fontSize: pxToRem(96),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(56),
            },
        },
        h2: {
            fontSize: pxToRem(60),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(38),
            },
        },
        h3: {
            fontSize: pxToRem(48),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(32),
            },
        },
        h4: {
            fontSize: pxToRem(28),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(20),
            },
        },
        h5: {
            fontSize: pxToRem(20),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(18),
            },
        },
        h6: {
            fontSize: pxToRem(18),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(16),
            },
        },
        subtitle1: {
            fontSize: pxToRem(16),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(14),
            },
        },
        subtitle2: {
            fontSize: pxToRem(14),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(10),
            },
        },
        body1: {
            fontSize: pxToRem(14),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(14),
            },
        },
        body2: {
            fontSize: pxToRem(14),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(16),
            },
        },
        button: {
            fontSize: pxToRem(16),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(14),
            },
        },
        caption: {
            fontSize: pxToRem(12),
            [customBreakpoints.down("sm")]: {
                fontSize: pxToRem(14),
            },
        },
    },
    ///***************
    ///Set up your material ui overrides here or in the index.scss file. Scss will probably be faster and easier overall
    ///***************
    overrides: {
        MuiFormHelperText: {
            root: {
                position: "absolute",
                bottom: "-22px",
            },
        },
        MuiDivider: {
            root: {
                width: "100%",
                margin: "30px 0px",
                borderTop: "1px dashed",
                color: "#7F7F7F",
            },
        },
        MuiButton: {
            contained: {
                boxShadow: "none",
            },
            root: {
                textTransform: "none",
                fontSize: pxToRem(14),
                "&$disabled": {
                    opacity: "0.5",
                },
            },
        },
        MuiIconButton: {
            label: {
                width: "32px",
                height: "32px",
            },
            root: {
                padding: "0px",
            },
        },
        PrivateSwitchBase: {
            root: {
                padding: "0px 9px",
            },
        },
        MuiSelect: {
            root: {
                // backgroundColor: "#ffffff !important",
                //backgroundColor: "green",
                fontWeight: "normal",
            },
            selectMenu: {
                width: "100%",
            },
            select: {
                width: "100%",
            },
        },
        MuiInput: {
            root: {
                width: "100%",
                marginTop: "5px",
                background: "#FFFFFF 0% 0% no-repeat padding-box",
                border: "1px solid #D4D4D4",
                backgroundColor: "#ffffff",
                // backgroundColor: "#ffffff !important", was this
                color: "#757474 !important",
                fontWeight: "normal",
                opacity: "1",
                textAlign: "left",
                letterSpacing: "0px",
            },
            input: {
                paddingLeft: "10px",
                paddingRight: "10px",
                "&:-internal-autofill-selected": {
                    backgroundColor: "#FFF !important",
                },
                "& .-internal-autofill-selected": {
                    backgroundColor: "#FFF !important",
                },
            },
            underline: {
                "&::before": {
                    border: "none !important",
                },
            },
        },
        MuiTextField: {
            root: {
                // width: "100%",
            },
        },
        MuiFormLabel: {
            root: {
                color: "rgba(0, 0, 0, 0.7)",
                textAlign: "left",
                letterSpacing: "0px",
                fontWeight: "bold",
                opacity: 1,
                // marginBottom: pxToRem(5),
                whiteSpace: "nowrap",
                [customBreakpoints.down("sm")]: {
                    whiteSpace: "normal",
                },
            },
        },
        MuiAccordionSummary: {
            content: { margin: "0px" },
            root: {},
        },
        MuiFormControl: {
            root: {
                // width: "100%",
                minWidth: "350px",
                [customBreakpoints.down("sm")]: {
                    minWidth: "auto",
                },
            },
        },

        MuiTableContainer: {
            root: { marginTop: "30px" },
        },
        MuiTableRow: {
            root: { borderBottom: "1px solid rgba(224, 224, 224, 1)" },
        },
        MuiInputLabel: {
            formControl: {
                top: "-8px",
                textAlign: "left",
                letterSpacing: "0px",
                fontSize: "12px !important",
                fontWeight: 600,
                opacity: 1,
                whiteSpace: "nowrap",
                transform: "none",
            },
            shrink: {
                transform: "none",
            },
        },
        /* MuiCheckbox: {
            root: {
                "& svg": {
                    border: "1px solid #CDCCCC",
                    borderRadius: "5px",
                    "& > path": {
                        display: "none",
                    },
                },
            },
            colorSecondary: {
                "&.Mui-checked svg": {
                    backgroundColor: (customPalette.secondary as SimplePaletteColorOptions).main,
                    "& > path": {
                        display: "initial",
                        fill: "white",
                        height: "20px",
                        width: "20px",
                        transform: "scale(1.4)",
                        transformOrigin: "50% 50%",
                    },
                },
            },
            colorPrimary: {
                "&.Mui-checked svg": {
                    backgroundColor: (customPalette.primary as SimplePaletteColorOptions).main,
                    "& > path": {
                        display: "initial",
                        fill: "white",
                        height: "20px",
                        width: "20px",
                        transform: "scale(1.4)",
                        transformOrigin: "50% 50%",
                    },
                },
            },
        },*/
    },
} as ThemeOptions);

///***************
///Add your Custom Colours below for typescript checking
///Probably only need to add to interface Palette as I have done here with accent
///***************
declare module "@material-ui/core/styles/createPalette" {
    interface IconPaletteColorOptions {
        pdf?: string;
        csv?: string;
    }
    interface IconPaletteColor {
        pdf: string;
        csv: string;
    }
    interface PaletteOptions {
        accent?: PaletteColorOptions;
        icon?: IconPaletteColorOptions;
    }
    ///***************
    ///Add your Custom Colours below for typescript
    ///***************
    interface Palette {
        hightownBlue: PaletteColor;
        hightownGreen: PaletteColor;
        purpleAccent: PaletteColor;
        customText: TypeText;
        icon: IconPaletteColor;
    }
}

let customPalette = {
    ///****************
    ///Customise the colours below to match your the designs
    ///****************
    common: { black: "#000", white: "#fff" },
    background: { paper: "#fff", default: "#F9F9F9" },
    primary: { light: "#6d6d6d", dark: "#1b1b1b", main: "#424242", contrastText: "#fff" },
    secondary: { light: "#FFFFFF", main: "#FCFCFC", dark: "#EDEDED", contrastText: "#000" },
    error: { light: "#e57373", main: "#f44336", dark: "#d32f2f", contrastText: "#fff" },
    warning: { light: "#ffb74d", main: "#ff9800", dark: "#f57c00", contrastText: "rgba(0, 0, 0, 0.87)" },
    info: { light: "#64b5f6", main: "#2196f3", dark: "#1976d2", contrastText: "#fff" },
    success: { light: "#81c784", main: "#4caf50", dark: "#388e3c", contrastText: "rgba(0, 0, 0, 0.87)" },
    text: {
        primary: "rgba(0,0,0, 0.87)",
        secondary: "rgba(0,0,0, 0.54)",
        disabled: "rgba(0,0,0, 0.38)",
        hint: "rgba(0,0,0, 0.38)",
    },
    divider: "rgba(0,0,0,0.12)",
    ///***************
    ///Add your Custom Colours below and in the declare module above this code block
    ///***************
    hightownBlue: { light: "#007AFF", main: "#4C8CA7", dark: "#388e3c", contrastText: "#FFFFFF" },
    hightownGreen: { light: "#81c784", main: "#84BB25", dark: "#3AAA35", contrastText: "#FFFFFF" },
    purpleAccent: { light: colors.purple[300], main: colors.purple[500], dark: colors.purple[700], contrastText: "#ffb74d" }, //This is a custom colour
    customText: {
        primary: "rgba(255,255,255, 0.87)",
        secondary: "rgba(255,255,255, 0.54)",
        disabled: "rgba(255,255,255, 0.38)",
        hint: "rgba(255,255,255, 0.38)",
    },
    icon: { csv: "rgba(255,255,255, 0.87)", pdf: "rgba(255,255,255, 0.87)" },
} as Palette;

export let theme = getTheme();
theme.breakpoints = customBreakpoints;
theme.palette = { ...theme.palette, ...customPalette };
export const palette = theme.palette;
window.theme = theme;
console.log("Theme done");
